<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>

    <!-- No strategy yet -->
    <div v-else-if="!strategy" class="text-center py-12">
      <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100 mb-2">
        AI Keyword Strategy
      </h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Generate a comprehensive keyword universe with competitor cross-referencing.
      </p>
      <button @click="handleRun" class="btn btn-primary">
        Generate Keyword Strategy
      </button>
    </div>

    <!-- Running -->
    <div v-else-if="strategy.status !== 'completed' && strategy.status !== 'failed'" class="text-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
      <p class="text-sm text-gray-600 dark:text-gray-400">
        {{ progressMessage || `Processing (${strategy.status})...` }}
      </p>
      <button @click="handleRun" class="btn btn-secondary btn-sm mt-4">
        Re-run Strategy
      </button>
    </div>

    <!-- Failed -->
    <div v-else-if="strategy.status === 'failed'" class="text-center py-12">
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">
        {{ strategy.error_message }}
      </p>
      <button @click="handleRun" class="btn btn-primary">
        Retry
      </button>
    </div>

    <!-- Completed: Step 1 — Universe Approval -->
    <div v-else-if="!strategy.keyword_universe?.approved" class="space-y-6">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100">
          Keyword Universe ({{ strategy.items?.length || 0 }} keywords)
        </h3>
        <button @click="handleApproveUniverse" class="btn btn-primary btn-sm">
          Approve Universe
        </button>
      </div>

      <form class="p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50" @submit.prevent="addManualUniverseKeyword">
        <label for="manual-strategy-keyword" class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
          Add manual keyword
        </label>
        <div class="flex flex-col sm:flex-row gap-2">
          <input
            id="manual-strategy-keyword"
            v-model="manualUniverseKeyword"
            type="text"
            placeholder="Keyword to include in the strategy"
            class="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
          <select
            v-model="manualUniverseTier"
            class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
            <option v-for="tier in tiers" :key="tier.key" :value="tier.key">
              {{ tier.label }}
            </option>
          </select>
          <button type="submit" class="btn btn-secondary btn-sm">
            Add
          </button>
        </div>
        <div v-if="addedKeywords.length" class="mt-3 flex flex-wrap gap-2">
          <span
            v-for="keyword in addedKeywords"
            :key="`${keyword.keyword}-${keyword.tier}`"
            class="inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs">
            {{ keyword.keyword }}
            <button type="button" class="text-primary-500 hover:text-red-500" @click="removeManualUniverseKeyword(keyword)">
              Remove
            </button>
          </span>
        </div>
      </form>

      <!-- Tier sections -->
      <div v-for="tier in tiers" :key="tier.key" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="toggleTier(tier.key)" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            {{ tier.label }} ({{ tierItems(tier.key).length }})
          </span>
          <span class="text-gray-400">{{ expandedTiers[tier.key] ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedTiers[tier.key]" class="p-4">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="pb-2">
                  Keyword
                </th>
                <th class="pb-2">
                  Volume
                </th>
                <th class="pb-2">
                  CPC
                </th>
                <th class="pb-2">
                  Source
                </th>
                <th class="pb-2" />
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in tierItems(tier.key)" :key="item.id" class="border-t border-gray-100 dark:border-gray-800">
                <td class="py-2 text-gray-900 dark:text-gray-100">
                  {{ item.keyword }}
                </td>
                <td class="py-2 text-gray-600 dark:text-gray-400">
                  {{ item.search_volume || '—' }}
                </td>
                <td class="py-2 text-gray-600 dark:text-gray-400">
                  {{ item.cpc ? `$${item.cpc}` : '—' }}
                </td>
                <td class="py-2">
                  <span class="px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {{ item.source }}
                  </span>
                </td>
                <td class="py-2 text-right">
                  <button @click="removeItem(item.id)" class="text-red-400 hover:text-red-600 text-xs">
                    Remove
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Completed: Step 2 — Bucket Cross-Reference -->
    <div v-else-if="!strategy.approved" class="space-y-6">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100">
          Cross-Reference Results
        </h3>
        <button @click="handleApproveCluster" class="btn btn-primary btn-sm">
          Approve Final Cluster
        </button>
      </div>

      <!-- Cannibalization warnings -->
      <div v-if="strategy.cannibalization_flags?.length" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
        <h4 class="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
          Cannibalization Risks
        </h4>
        <div v-for="flag in strategy.cannibalization_flags" :key="flag.keyword" class="text-sm text-red-700 dark:text-red-400">
          "{{ flag.keyword }}" may belong to {{ flag.sibling_page }} ({{ flag.sibling_slug }})
        </div>
      </div>

      <div
        v-for="bucket in bucketSections"
        :key="bucket.key"
        :class="bucket.borderClass"
        class="rounded-xl p-4">
        <h4 :class="bucket.headingClass" class="text-sm font-medium mb-3">
          {{ bucket.label }} ({{ bucketItems(bucket.key).length }})
        </h4>
        <div v-if="bucketItems(bucket.key).length === 0" class="text-sm text-gray-500 dark:text-gray-400">
          No keywords in this bucket.
        </div>
        <div v-for="item in bucketItems(bucket.key)" :key="item.id" class="flex items-center justify-between gap-3 py-2 border-t border-gray-100 dark:border-gray-800 first:border-0">
          <div>
            <span class="text-sm text-gray-900 dark:text-gray-100">{{ item.keyword }}</span>
            <p v-if="item.recommendation_reasoning" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ item.recommendation_reasoning }}
            </p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Default: {{ recommendationLabel(item) }}
            </p>
          </div>
          <select
            :value="decisionSelectValue(item)"
            @change="handleItemDecision(item.id, $event.target.value)"
            class="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-800">
            <option value="">
              Use default
            </option>
            <option value="included">
              Include
            </option>
            <option value="excluded">
              Exclude
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Approved — show strategy summary -->
    <div v-else class="space-y-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-green-500">&#10003;</span>
          <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100">
            Keyword Strategy ({{ approvedItems.length }} keywords)
          </h3>
        </div>
        <button @click="handleRun" class="btn btn-secondary btn-sm">
          Re-run Strategy
        </button>
      </div>

      <!-- Tier sections (read-only) -->
      <div v-for="tier in tiers" :key="tier.key">
        <div
          v-if="approvedTierItems(tier.key).length"
          class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <button @click="toggleTier(tier.key)" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
            <span class="font-medium text-gray-900 dark:text-gray-100">
              {{ tier.label }} ({{ approvedTierItems(tier.key).length }})
            </span>
            <span class="text-gray-400">{{ expandedTiers[tier.key] ? '−' : '+' }}</span>
          </button>
          <div v-if="expandedTiers[tier.key]" class="p-4">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                  <th class="pb-2">
                    Keyword
                  </th>
                  <th class="pb-2">
                    Volume
                  </th>
                  <th class="pb-2">
                    CPC
                  </th>
                  <th class="pb-2">
                    Bucket
                  </th>
                  <th class="pb-2">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in approvedTierItems(tier.key)" :key="item.id" class="border-t border-gray-100 dark:border-gray-800">
                  <td class="py-2 text-gray-900 dark:text-gray-100">
                    {{ item.keyword }}
                  </td>
                  <td class="py-2 text-gray-600 dark:text-gray-400">
                    {{ item.search_volume ? item.search_volume.toLocaleString() : '—' }}
                  </td>
                  <td class="py-2 text-gray-600 dark:text-gray-400">
                    {{ item.cpc ? `$${item.cpc}` : '—' }}
                  </td>
                  <td class="py-2">
                    <span v-if="item.bucket" :class="bucketClass(item.bucket)" class="px-2 py-0.5 rounded-lg text-xs">
                      {{ bucketLabel(item.bucket) }}
                    </span>
                  </td>
                  <td class="py-2">
                    <span class="px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {{ item.source }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Cannibalization warnings if any -->
      <div v-if="strategy.cannibalization_flags?.length" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
        <h4 class="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
          Cannibalization Risks
        </h4>
        <div v-for="flag in strategy.cannibalization_flags" :key="flag.keyword" class="text-sm text-red-700 dark:text-red-400">
          "{{ flag.keyword }}" may belong to {{ flag.sibling_page }} ({{ flag.sibling_slug }})
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { runKeywordStrategy, getKeywordStrategy, approveKeywordUniverse, approveKeywordCluster } from '~/javascript/api/keyword_strategy.js'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['approved'])

let subscription = null
let pollTimer = null

const loading = ref(true)
const strategy = ref(null)
const progressMessage = ref('')
const expandedTiers = ref({ core: true, high_value: true, supporting: false, long_tail: false })
const itemDecisions = ref({})
const removedIds = ref([])
const addedKeywords = ref([])
const manualUniverseKeyword = ref('')
const manualUniverseTier = ref('supporting')

const tiers = [
  { key: 'core', label: 'Core Keywords' },
  { key: 'high_value', label: 'High-Value Secondary' },
  { key: 'supporting', label: 'Supporting Keywords' },
  { key: 'long_tail', label: 'Long-Tail Keywords' }
]

const bucketSections = [
  {
    key: 'a',
    label: 'Bucket A — Shared Keywords',
    borderClass: 'border border-primary-200 dark:border-primary-800',
    headingClass: 'text-primary-700 dark:text-primary-400'
  },
  {
    key: 'b',
    label: 'Bucket B — Opportunity Gaps',
    borderClass: 'border border-green-200 dark:border-green-800',
    headingClass: 'text-green-700 dark:text-green-400'
  },
  {
    key: 'c',
    label: 'Bucket C — Competitor-Only',
    borderClass: 'border border-amber-200 dark:border-amber-800',
    headingClass: 'text-amber-700 dark:text-amber-400'
  }
]

const isIncludedItem = (item) => {
  const decision = item.user_decision
  if (decision === 'included') return true
  if (decision === 'excluded') return false
  return item.include_recommendation === 'include' || item.include_recommendation == null
}

const tierItems = (tier) => (strategy.value?.items || []).filter((i) => i.tier === tier && !removedIds.value.includes(i.id))
const approvedItems = computed(() => (strategy.value?.items || []).filter((i) => isIncludedItem(i)))
const approvedTierItems = (tier) => approvedItems.value.filter((i) => i.tier === tier)
const bucketItems = (bucket) => (strategy.value?.items || []).filter((i) => i.bucket === bucket)
const toggleTier = (key) => { expandedTiers.value[key] = !expandedTiers.value[key] }

const bucketClass = (bucket) => {
  const map = {
    a: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    b: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    c: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
  }
  return map[bucket] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}

const bucketLabel = (bucket) => {
  const map = { a: 'Shared', b: 'Opportunity', c: 'Competitor' }
  return map[bucket] || bucket
}

const removeItem = (id) => { removedIds.value.push(id) }

const handleItemDecision = (itemId, decision) => {
  itemDecisions.value[itemId] = decision || null
}

const decisionSelectValue = (item) => {
  if (Object.prototype.hasOwnProperty.call(itemDecisions.value, item.id)) {
    return itemDecisions.value[item.id] || ''
  }

  return item.user_decision || ''
}

const recommendationLabel = (item) => {
  if (item.include_recommendation === 'include') return 'Include'
  if (item.include_recommendation === 'exclude') return 'Exclude'
  if (item.include_recommendation === 'evaluate') return 'Evaluate manually'
  return 'Include'
}

const addManualUniverseKeyword = () => {
  const keyword = manualUniverseKeyword.value.trim()
  if (!keyword) return
  const exists = addedKeywords.value.some((item) => item.keyword.toLowerCase() === keyword.toLowerCase()) ||
    (strategy.value?.items || []).some((item) => item.keyword.toLowerCase() === keyword.toLowerCase())
  if (!exists) {
    addedKeywords.value.push({ keyword, tier: manualUniverseTier.value })
  }
  manualUniverseKeyword.value = ''
}

const removeManualUniverseKeyword = (keyword) => {
  addedKeywords.value = addedKeywords.value.filter((item) => item !== keyword)
}

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const fetchStrategy = async() => {
  try {
    loading.value = true
    const response = await getKeywordStrategy(props.pageId)
    strategy.value = response.data.keyword_strategy
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching strategy:', e)
  } finally {
    loading.value = false
  }
}

const startPolling = () => {
  stopPolling()
  pollTimer = setInterval(async() => {
    try {
      const response = await getKeywordStrategy(props.pageId)
      const s = response.data.keyword_strategy
      if (s && (s.status === 'completed' || s.status === 'failed')) {
        strategy.value = s
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

const subscribeToChannel = () => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'KeywordStrategyChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'completed' || data.step === 'failed') {
          stopPolling()
          fetchStrategy()
        }
      }
    }
  )
}

const handleRun = async() => {
  try {
    await runKeywordStrategy(props.pageId)
    subscribeToChannel()
    strategy.value = { status: 'generating' }
    loading.value = false
    startPolling()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to start strategy:', e)
  }
}

const handleApproveUniverse = async() => {
  try {
    const response = await approveKeywordUniverse(props.pageId, {
      addedKeywords: addedKeywords.value,
      removedItemIds: removedIds.value
    })
    strategy.value = response.data.keyword_strategy
    addedKeywords.value = []
    removedIds.value = []
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to approve universe:', e)
  }
}

const handleApproveCluster = async() => {
  try {
    const response = await approveKeywordCluster(props.pageId, itemDecisions.value)
    strategy.value = response.data.keyword_strategy
    emit('approved')
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to approve cluster:', e)
  }
}

onMounted(async() => {
  await fetchStrategy()
  subscribeToChannel()
  // If strategy is in a non-terminal state, start polling
  if (strategy.value && strategy.value.status !== 'completed' && strategy.value.status !== 'failed') {
    startPolling()
  }
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
